import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { AppShell } from "@/components/AppShell";
import { StatusBadge, CompanyAvatar } from "@/components/StatusBadge";
import {
  BOLAG,
  FASER,
  STATUSAR,
  MOGNADSSKALA,
  formatSEK,
  totalKapital,
  portfolioRLRorelse,
  type Fas,
  type Bransch,
  type BolagStatus,
} from "@/lib/portfolio-data";

export const Route = createFileRoute("/portfolj")({
  head: () => ({
    meta: [
      { title: "Portfölj — Inkubera" },
      { name: "description", content: "Hela Inkuberas bolagsportfölj med filter på fas, bransch och mognad." },
    ],
  }),
  component: Portfolj,
});

const ALLA_BRANSCHER: Bransch[] = [
  "AI", "SaaS", "Deeptech", "Healthtech", "Medtech", "Greentech",
  "Industri", "Digitalisering", "Edtech", "Fintech", "Mobility", "Energi", "Life Science",
];

function Portfolj() {
  const [fas, setFas] = useState<Fas | "Alla">("Alla");
  const [bransch, setBransch] = useState<Bransch | "Alla">("Alla");
  const [status, setStatus] = useState<BolagStatus | "Alla">("Alla");
  const [sok, setSok] = useState("");

  const filtered = useMemo(
    () =>
      BOLAG.filter((b) => {
        const bolagStatus: BolagStatus = b.status ?? "Aktivt";
        return (
          (fas === "Alla" || b.fas === fas) &&
          (bransch === "Alla" || b.branscher.includes(bransch)) &&
          (status === "Alla" || bolagStatus === status) &&
          (sok === "" || b.namn.toLowerCase().includes(sok.toLowerCase()))
        );
      }),
    [fas, bransch, status, sok],
  );

  return (
    <AppShell>
      <section className="px-8 pt-10 pb-6 border-b border-border">
        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">Portfölj</div>
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <h1 className="font-display text-4xl">{filtered.length} bolag</h1>
          <input
            value={sok}
            onChange={(e) => setSok(e.target.value)}
            placeholder="Filtrera på namn…"
            className="h-10 px-4 rounded-lg bg-secondary border border-transparent focus:border-ring focus:outline-none text-sm w-72"
          />
        </div>

        <div className="mt-6 space-y-3">
          <FilterRow
            label="Status"
            options={["Alla", ...STATUSAR]}
            value={status}
            onChange={(v) => setStatus(v as BolagStatus | "Alla")}
          />
          <FilterRow
            label="Fas"
            options={["Alla", ...FASER]}
            value={fas}
            onChange={(v) => setFas(v as Fas | "Alla")}
          />
          <FilterRow
            label="Bransch"
            options={["Alla", ...ALLA_BRANSCHER]}
            value={bransch}
            onChange={(v) => setBransch(v as Bransch | "Alla")}
          />
        </div>
      </section>

      <RLPortfolioKPI />

      <section className="px-8 py-8">
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-secondary/40">
              <tr>
                <th className="px-5 py-3 font-medium">Bolag</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Fas</th>
                <th className="px-5 py-3 font-medium">Mognad</th>
                <th className="px-5 py-3 font-medium">Branscher</th>
                <th className="px-5 py-3 font-medium text-right">Kapital</th>
                <th className="px-5 py-3 font-medium text-right">Hälsa</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr
                  key={b.id}
                  className="border-t border-border hover:bg-secondary/40 transition-colors group"
                >
                  <td className="px-5 py-3">
                    <Link to="/bolag/$id" params={{ id: b.id }} className="flex items-center gap-3">
                      <CompanyAvatar initialer={b.initialer} farg={b.logoFarg} size={36} />
                      <div className="min-w-0">
                        <div className="font-medium group-hover:underline">{b.namn}</div>
                        <div className="text-xs text-muted-foreground">{b.ort} · {b.affarsutvecklare}</div>
                      </div>
                    </Link>
                  </td>
                  <td className="px-5 py-3"><StatusPill status={b.status ?? "Aktivt"} /></td>
                  <td className="px-5 py-3"><StatusBadge fas={b.fas} /></td>
                  <td className="px-5 py-3">
                    <MognadBar mognad={b.mognad} />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {b.branscher.slice(0, 3).map((br) => (
                        <span key={br} className="text-[11px] px-2 py-0.5 rounded-full bg-secondary">{br}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right font-medium">{formatSEK(totalKapital(b))}</td>
                  <td className="px-5 py-3 text-right">
                    <HealthDot score={b.hälsoscore} />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-muted-foreground">Inga träffar</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </AppShell>
  );
}

function FilterRow({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground w-16 shrink-0">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button
            key={o}
            onClick={() => onChange(o)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              value === o
                ? "bg-foreground text-background border-foreground"
                : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function MognadBar({ mognad }: { mognad: string }) {
  const idx = MOGNADSSKALA.indexOf(mognad as never);
  const pct = ((idx + 1) / MOGNADSSKALA.length) * 100;
  return (
    <div className="w-40">
      <div className="text-[11px] text-muted-foreground mb-1">{mognad}</div>
      <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
        <div className="h-full rounded-full bg-foreground" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function HealthDot({ score }: { score: number }) {
  const color =
    score >= 80 ? "var(--success)" : score >= 60 ? "var(--warning)" : "var(--destructive)";
  return (
    <div className="inline-flex items-center gap-2">
      <span className="size-2 rounded-full" style={{ background: color }} />
      <span className="font-medium">{score}</span>
    </div>
  );
}

function StatusPill({ status }: { status: BolagStatus }) {
  const styles: Record<BolagStatus, string> = {
    Aktivt: "bg-[oklch(0.78_0.16_140)/0.15] text-[oklch(0.55_0.18_140)] border-[oklch(0.78_0.16_140)/0.4]",
    Inaktivt: "bg-secondary text-muted-foreground border-border",
    Avvecklat: "bg-[oklch(0.7_0.04_30)/0.15] text-[oklch(0.5_0.06_30)] border-[oklch(0.7_0.04_30)/0.4]",
    Konkurs: "bg-[oklch(0.7_0.2_25)/0.15] text-[oklch(0.55_0.2_25)] border-[oklch(0.7_0.2_25)/0.4]",
    Uppköpt: "bg-[oklch(0.7_0.18_280)/0.15] text-[oklch(0.55_0.18_280)] border-[oklch(0.7_0.18_280)/0.4]",
  };
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${styles[status]}`}>{status}</span>
  );
}

function RLPortfolioKPI() {
  const k = portfolioRLRorelse();
  const items = [
    { label: "Bolag mätta", value: k.antal },
    { label: "Snitt RL-rörelse / bolag", value: k.snittTotal >= 0 ? `+${k.snittTotal}` : `${k.snittTotal}` },
    { label: "Snitt per RL-nivå", value: k.snittPerNivaa >= 0 ? `+${k.snittPerNivaa}` : `${k.snittPerNivaa}` },
    { label: "Andel som rört sig framåt", value: `${k.andelMedRorelse}%` },
  ];
  return (
    <section className="px-8 pt-8">
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-lg">Inkubator-KPI · RL-förflyttning</h2>
            <p className="text-xs text-muted-foreground">
              Hur väl vi förflyttar bolagen i Business / Market / Sustainability / Technology Readiness Levels.
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {items.map((it) => (
            <div key={it.label} className="rounded-xl bg-secondary/40 p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{it.label}</div>
              <div className="font-display text-2xl mt-1.5">{it.value}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
